import { DragEvent, useRef, useState } from "react";

/** Reposiciona os itens de `lista` cujo id está em `ids`, na ordem de `ids`, mantendo os demais no lugar. */
export function aplicarOrdem<T extends { id: string; ordem: number }>(lista: T[], ids: string[]): T[] {
  const porId = new Map(lista.map((i) => [i.id, i]));
  let posicao = 0;
  return lista.map((item) => {
    if (!ids.includes(item.id)) return item;
    const proximo = porId.get(ids[posicao])!;
    posicao += 1;
    return { ...proximo, ordem: posicao - 1 };
  });
}

/**
 * Arrastar para reordenar com HTML5 DnD nativo. `ids` é a sequência atual do grupo;
 * `onMover` atualiza a tela a cada passo e `onSalvar` persiste ao soltar.
 */
export function useOrdenavel(
  ids: string[],
  onMover: (ids: string[]) => void,
  onSalvar: (ids: string[]) => Promise<void>
) {
  const [arrastando, setArrastando] = useState<string | null>(null);
  const atual = useRef<string[]>(ids);
  const inicial = useRef<string[]>(ids);

  function propsDaLinha(id: string) {
    return {
      draggable: true,
      onDragStart: (e: DragEvent) => {
        e.dataTransfer.effectAllowed = "move";
        atual.current = ids;
        inicial.current = ids;
        setArrastando(id);
      },
      onDragOver: (e: DragEvent) => {
        if (!arrastando) return;
        e.preventDefault();
        if (arrastando === id) return;
        const base = atual.current.filter((x) => x !== arrastando);
        const alvo = base.indexOf(id);
        if (alvo < 0) return;
        const origem = atual.current.indexOf(arrastando);
        const destino = origem < atual.current.indexOf(id) ? alvo + 1 : alvo;
        base.splice(destino, 0, arrastando);
        if (base.join() === atual.current.join()) return;
        atual.current = base;
        onMover(base);
      },
      onDrop: (e: DragEvent) => e.preventDefault(),
      onDragEnd: () => {
        setArrastando(null);
        if (atual.current.join() !== inicial.current.join()) void onSalvar(atual.current);
      },
    };
  }

  return { arrastando, propsDaLinha };
}
